/**
 * Публичная главная (ТЗ §10.5): компактная шапка, короткий заголовок без
 * рекламной гиперболы, одно основное действие. Показателей платформы нет —
 * придумывать их ТЗ прямо запрещает (§10.8).
 */
import { Link } from 'react-router-dom'
import { GraduationCap } from 'lucide-react'

import { useAuth } from '@/features/auth/useAuth'
import { Button } from '@/shared/ui/Button'
import { ThemeToggle } from '@/shared/ui/ThemeToggle'

const SUBJECTS = [
  'Математика',
  'Русский язык',
  'Физика',
  'Информатика',
  'Химия',
  'Биология',
  'История',
  'Обществознание',
]

const STEPS = [
  { title: 'Теория', text: 'Материалы по теме с формулами и примерами.' },
  { title: 'Практика', text: 'Задания разных типов с автопроверкой.' },
  { title: 'Проверка', text: 'Развёрнутые ответы читает учитель.' },
  { title: 'Работа над ошибками', text: 'Разбор того, что не получилось.' },
]

export function HomePage() {
  const { user } = useAuth()

  return (
    <div className="min-h-dvh bg-bg">
      <header className="border-b border-line">
        <div className="shell flex h-16 items-center justify-between">
          <span className="flex items-center gap-2 font-semibold text-ink">
            <GraduationCap aria-hidden className="size-5 text-accent" />
            Учусь тут
          </span>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <Link to="/кабинет">
                <Button size="sm">В кабинет</Button>
              </Link>
            ) : (
              <Link to="/вход" className="text-meta text-ink-soft hover:text-ink">
                Войти
              </Link>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="shell py-16 sm:py-24">
          <div className="max-w-prose">
            <h1 className="text-3xl font-bold leading-tight sm:text-4xl">
              Учебные классы для подготовки к ОГЭ и ЕГЭ
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={user ? '/кабинет' : '/регистрация'}>
                <Button>{user ? 'Перейти в кабинет' : 'Начать обучение'}</Button>
              </Link>
              {!user && (
                <Link
                  to="/регистрация?роль=учитель"
                  className="text-meta text-ink-soft underline-offset-4 hover:text-ink hover:underline"
                >
                  Создать класс
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className="border-t border-line py-12">
          <div className="shell">
            <h2 className="text-label font-semibold uppercase tracking-wide text-ink-muted">
              Предметы
            </h2>
            <ul className="mt-4 grid grid-cols-2 gap-x-6 sm:grid-cols-3 lg:grid-cols-4">
              {SUBJECTS.map((subject) => (
                <li key={subject} className="border-b border-line py-3 text-meta text-ink-soft">
                  {subject}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-t border-line py-12">
          <div className="shell">
            <h2 className="text-label font-semibold uppercase tracking-wide text-ink-muted">
              Как это работает
            </h2>
            <ol className="mt-4 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((step, index) => (
                <li key={step.title}>
                  <span className="font-mono text-label text-accent">0{index + 1}</span>
                  <h3 className="mt-1 text-base font-semibold">{step.title}</h3>
                  <p className="mt-1 text-meta text-ink-soft">{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </main>

      <footer className="border-t border-line py-8">
        <div className="shell text-label text-ink-muted">
          Учусь тут · u.sch.tg
        </div>
      </footer>
    </div>
  )
}
